import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Mail, Phone, Check, Archive, Trash2, Search, Filter, 
  ChevronLeft, ChevronRight, Eye, Reply, MailOpen, RefreshCw,
  Calendar, MessageSquare
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` }
});

const ITEMS_PER_PAGE = 10;

const AdminContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [viewContact, setViewContact] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);

  useEffect(() => {
    fetchContacts();
  }, []);

  // Apply filters
  useEffect(() => {
    let filtered = [...contacts];
    
    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(c => c.status === statusFilter);
    }
    
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(c => 
        c.name?.toLowerCase().includes(term) ||
        c.email?.toLowerCase().includes(term) ||
        c.subject?.toLowerCase().includes(term) ||
        c.message?.toLowerCase().includes(term)
      );
    }
    
    setFilteredContacts(filtered);
    setCurrentPage(1); // Reset to first page when filter changes
  }, [contacts, statusFilter, searchTerm]);

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/admin/contacts`, getAuthHeaders());
      setContacts(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/contacts/${id}`, { status }, getAuthHeaders());
      toast.success("Statut mis à jour");
      fetchContacts();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, getAuthHeaders());
      toast.success("Message supprimé");
      setDeleteDialogOpen(false);
      setContactToDelete(null);
      fetchContacts();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleBulkStatusUpdate = async (status) => {
    try {
      await Promise.all(
        selectedContacts.map(id => 
          axios.put(`${API}/admin/contacts/${id}`, { status }, getAuthHeaders())
        )
      );
      toast.success(`${selectedContacts.length} messages mis à jour`);
      setSelectedContacts([]);
      fetchContacts();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleBulkDelete = async () => {
    try {
      await Promise.all(
        selectedContacts.map(id => 
          axios.delete(`${API}/admin/contacts/${id}`, getAuthHeaders())
        )
      );
      toast.success(`${selectedContacts.length} messages supprimés`);
      setSelectedContacts([]);
      setBulkDeleteDialogOpen(false);
      fetchContacts();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const toggleSelectContact = (id) => {
    setSelectedContacts(prev => 
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    const pageContacts = getCurrentPageContacts().map(c => c.id);
    if (pageContacts.every(id => selectedContacts.includes(id))) {
      setSelectedContacts(prev => prev.filter(id => !pageContacts.includes(id)));
    } else {
      setSelectedContacts(prev => [...new Set([...prev, ...pageContacts])]);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      new: "bg-red-100 text-red-800 border-red-200",
      read: "bg-yellow-100 text-yellow-800 border-yellow-200",
      replied: "bg-green-100 text-green-800 border-green-200",
      archived: "bg-gray-100 text-gray-800 border-gray-200",
    };
    const labels = {
      new: "Nouveau",
      read: "Lu",
      replied: "Répondu",
      archived: "Archivé",
    };
    const icons = {
      new: <Mail className="h-3 w-3" />,
      read: <MailOpen className="h-3 w-3" />,
      replied: <Reply className="h-3 w-3" />,
      archived: <Archive className="h-3 w-3" />,
    };
    return (
      <Badge className={`${colors[status] || "bg-gray-100"} gap-1 border`}>
        {icons[status]}
        {labels[status] || status}
      </Badge>
    );
  };

  // Pagination
  const totalPages = Math.ceil(filteredContacts.length / ITEMS_PER_PAGE);
  const getCurrentPageContacts = () => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredContacts.slice(start, start + ITEMS_PER_PAGE);
  };

  // Stats
  const stats = {
    total: contacts.length,
    new: contacts.filter(c => c.status === "new").length,
    read: contacts.filter(c => c.status === "read").length,
    replied: contacts.filter(c => c.status === "replied").length,
    archived: contacts.filter(c => c.status === "archived").length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="admin-contacts">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="font-serif text-3xl font-bold">Messages</h1>
          <p className="text-muted-foreground">Demandes de contact reçues</p>
        </div>
        <Button variant="outline" onClick={fetchContacts} className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Actualiser
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Card className="cursor-pointer hover:border-primary transition-colors" onClick={() => setStatusFilter("all")}>
          <CardContent className="p-4 text-center">
            <MessageSquare className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
            <p className="text-2xl font-bold">{stats.total}</p>
            <p className="text-xs text-muted-foreground">Total</p>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer hover:border-red-500 transition-colors ${statusFilter === "new" ? "border-red-500" : ""}`} onClick={() => setStatusFilter("new")}>
          <CardContent className="p-4 text-center">
            <Mail className="h-6 w-6 mx-auto mb-2 text-red-500" />
            <p className="text-2xl font-bold text-red-600">{stats.new}</p>
            <p className="text-xs text-muted-foreground">Nouveaux</p>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer hover:border-yellow-500 transition-colors ${statusFilter === "read" ? "border-yellow-500" : ""}`} onClick={() => setStatusFilter("read")}>
          <CardContent className="p-4 text-center">
            <MailOpen className="h-6 w-6 mx-auto mb-2 text-yellow-500" />
            <p className="text-2xl font-bold text-yellow-600">{stats.read}</p>
            <p className="text-xs text-muted-foreground">Lus</p>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer hover:border-green-500 transition-colors ${statusFilter === "replied" ? "border-green-500" : ""}`} onClick={() => setStatusFilter("replied")}>
          <CardContent className="p-4 text-center">
            <Reply className="h-6 w-6 mx-auto mb-2 text-green-500" />
            <p className="text-2xl font-bold text-green-600">{stats.replied}</p>
            <p className="text-xs text-muted-foreground">Répondus</p>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer hover:border-gray-500 transition-colors ${statusFilter === "archived" ? "border-gray-500" : ""}`} onClick={() => setStatusFilter("archived")}>
          <CardContent className="p-4 text-center">
            <Archive className="h-6 w-6 mx-auto mb-2 text-gray-500" />
            <p className="text-2xl font-bold text-gray-600">{stats.archived}</p>
            <p className="text-xs text-muted-foreground">Archivés</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher par nom, email, sujet..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="new">Nouveaux</SelectItem>
            <SelectItem value="read">Lus</SelectItem>
            <SelectItem value="replied">Répondus</SelectItem>
            <SelectItem value="archived">Archivés</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Bulk Actions */}
      {selectedContacts.length > 0 && (
        <div className="flex items-center gap-4 mb-4 p-4 bg-muted rounded-lg">
          <span className="text-sm font-medium">{selectedContacts.length} sélectionné(s)</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => handleBulkStatusUpdate("read")}>
              <MailOpen className="h-4 w-4 mr-1" /> Marquer lu
            </Button>
            <Button size="sm" variant="outline" onClick={() => handleBulkStatusUpdate("replied")}>
              <Check className="h-4 w-4 mr-1" /> Marquer répondu
            </Button>
            <Button size="sm" variant="outline" onClick={() => handleBulkStatusUpdate("archived")}>
              <Archive className="h-4 w-4 mr-1" /> Archiver
            </Button>
            <Button size="sm" variant="destructive" onClick={() => setBulkDeleteDialogOpen(true)}>
              <Trash2 className="h-4 w-4 mr-1" /> Supprimer
            </Button>
          </div>
        </div>
      )}

      {/* Messages List */}
      {filteredContacts.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          {searchTerm || statusFilter !== "all" ? "Aucun message ne correspond aux filtres" : "Aucun message reçu"}
        </div>
      ) : (
        <>
          {/* Select All */}
          <div className="flex items-center gap-2 mb-4">
            <Checkbox
              checked={getCurrentPageContacts().every(c => selectedContacts.includes(c.id))}
              onCheckedChange={toggleSelectAll}
            />
            <span className="text-sm text-muted-foreground">Sélectionner tout sur cette page</span>
          </div>

          <div className="space-y-4">
            {getCurrentPageContacts().map((contact) => (
              <Card 
                key={contact.id} 
                className={`transition-all ${contact.status === "new" ? "border-red-300 bg-red-50/30" : ""} ${selectedContacts.includes(contact.id) ? "ring-2 ring-primary" : ""}`}
              >
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-start gap-4">
                    {/* Checkbox */}
                    <Checkbox
                      checked={selectedContacts.includes(contact.id)}
                      onCheckedChange={() => toggleSelectContact(contact.id)}
                      className="mt-1"
                    />
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-3 gap-4">
                        <div className="min-w-0">
                          <h3 className="font-semibold text-lg truncate">{contact.name}</h3>
                          <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground mt-1">
                            <a href={`mailto:${contact.email}`} className="flex items-center gap-1 hover:text-primary truncate">
                              <Mail className="h-4 w-4 flex-shrink-0" />
                              {contact.email}
                            </a>
                            {contact.phone && (
                              <a href={`tel:${contact.phone}`} className="flex items-center gap-1 hover:text-primary">
                                <Phone className="h-4 w-4 flex-shrink-0" />
                                {contact.phone}
                              </a>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {getStatusBadge(contact.status)}
                        </div>
                      </div>
                      
                      <div className="mb-4">
                        <p className="font-medium mb-1">{contact.subject}</p>
                        <p className="text-muted-foreground line-clamp-2">{contact.message}</p>
                      </div>

                      <div className="flex flex-wrap items-center justify-between gap-4">
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(contact.created_at).toLocaleString("fr-FR")}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <Button size="sm" variant="ghost" onClick={() => setViewContact(contact)}>
                            <Eye className="h-4 w-4 mr-1" /> Voir
                          </Button>
                          {contact.status === "new" && (
                            <Button size="sm" variant="outline" onClick={() => handleUpdateStatus(contact.id, "read")}>
                              <MailOpen className="h-4 w-4 mr-1" /> Lu
                            </Button>
                          )}
                          {contact.status !== "replied" && contact.status !== "archived" && (
                            <Button size="sm" variant="outline" onClick={() => handleUpdateStatus(contact.id, "replied")}>
                              <Check className="h-4 w-4 mr-1" /> Répondu
                            </Button>
                          )}
                          {contact.status !== "archived" && (
                            <Button size="sm" variant="outline" onClick={() => handleUpdateStatus(contact.id, "archived")}>
                              <Archive className="h-4 w-4 mr-1" /> Archiver
                            </Button>
                          )}
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => { setContactToDelete(contact); setDeleteDialogOpen(true); }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <p className="text-sm text-muted-foreground">
                Page {currentPage} sur {totalPages} ({filteredContacts.length} messages)
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" /> Précédent
                </Button>
                <div className="flex gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCurrentPage(pageNum)}
                        className="w-9"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Suivant <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* View Contact Dialog */}
      <Dialog open={!!viewContact} onOpenChange={() => setViewContact(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Message de {viewContact?.name}</DialogTitle>
            <DialogDescription>
              Reçu le {viewContact && new Date(viewContact.created_at).toLocaleString("fr-FR")}
            </DialogDescription>
          </DialogHeader>
          {viewContact && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <a href={`mailto:${viewContact.email}`} className="font-medium hover:text-primary">
                    {viewContact.email}
                  </a>
                </div>
                {viewContact.phone && (
                  <div>
                    <p className="text-sm text-muted-foreground">Téléphone</p>
                    <a href={`tel:${viewContact.phone}`} className="font-medium hover:text-primary">
                      {viewContact.phone}
                    </a>
                  </div>
                )}
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Sujet</p>
                <p className="font-medium">{viewContact.subject}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Message</p>
                <div className="bg-muted p-4 rounded-lg whitespace-pre-wrap">
                  {viewContact.message}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div>{getStatusBadge(viewContact.status)}</div>
                <div className="flex gap-2">
                  <Button variant="outline" asChild>
                    <a href={`mailto:${viewContact.email}?subject=Re: ${viewContact.subject}`}>
                      <Reply className="h-4 w-4 mr-2" /> Répondre par email
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce message ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le message de {contactToDelete?.name} sera définitivement supprimé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction 
              className="bg-red-500 hover:bg-red-600"
              onClick={() => handleDelete(contactToDelete?.id)}
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer {selectedContacts.length} messages ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Tous les messages sélectionnés seront définitivement supprimés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction 
              className="bg-red-500 hover:bg-red-600"
              onClick={handleBulkDelete}
            >
              Supprimer tout
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminContacts;
